import React, { useEffect, useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import { PublishedBroadcast } from '../../../services/broadcastService'
import { getPartnerLogoUrl } from '../../../services/partnerService'
import { currencyOf, rateFor, valueOf, formatMoney, formatAreaSeconds } from '../broadcastValuation'
import { colourMap } from './BroadcastValueDonuts'

interface Props {
  broadcasts: PublishedBroadcast[]
  customerId: string
  /** Wired up when the sponsor drill-down lands; until then cards are static. */
  onSelect?: (partnerId: string) => void
}

export interface SponsorRollup {
  id: string
  name: string
  area: number
  /** Null when none of this sponsor's broadcasts is priced. */
  value: number | null
  broadcasts: number
  priced: number
  currency: string
}

/**
 * One sponsor's footprint across the library, built from the same per-broadcast
 * sponsor_area slices the donut reads, so a card and its donut slice always
 * agree. Value is priced per broadcast at that broadcast's own rate and summed
 * over the priced ones only; area counts every broadcast.
 */
export const rollup = (broadcasts: PublishedBroadcast[]): SponsorRollup[] => {
  const acc = new Map<string, SponsorRollup>()
  for (const b of broadcasts) {
    const rate = rateFor(b)
    for (const s of b.sponsor_area ?? []) {
      const area = Number(s.area_seconds ?? 0)
      if (area <= 0) continue
      const r = acc.get(s.key) ?? {
        id: s.key, name: s.label ?? 'Unnamed sponsor', area: 0, value: null,
        broadcasts: 0, priced: 0, currency: currencyOf(b)
      }
      r.area += area
      r.broadcasts += 1
      if (rate !== null) {
        r.value = (r.value ?? 0) + (valueOf(area, rate).value ?? 0)
        r.priced += 1
        r.currency = currencyOf(b)
      }
      acc.set(s.key, r)
    }
  }
  // Value first, then area: an unpriced sponsor is missing information, not
  // the least valuable one, so it sorts after the priced ones by its exposure.
  return [...acc.values()].sort((a, b) =>
    (b.value ?? -1) - (a.value ?? -1) || b.area - a.area)
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')

/** The partner logo through the CRO proxy; initials when there is none. */
export const SponsorLogo: React.FC<{ id: string; name: string; customerId: string; size?: string }> = ({ id, name, customerId, size = 'h-12 w-12' }) => {
  const [failed, setFailed] = useState(false)
  return (
    <div className={`${size} flex-shrink-0 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden`}>
      {failed ? (
        <span className="text-sm font-semibold text-gray-500">{initials(name)}</span>
      ) : (
        <img src={getPartnerLogoUrl(id, customerId)} alt={`${name} logo`}
             className="max-h-full max-w-full object-contain p-1"
             onError={() => setFailed(true)} />
      )}
    </div>
  )
}

const OTHER_COLOR = '#b4b2a9'

/**
 * Counts up from zero to `target` once, on mount. Off under reduced motion,
 * where the number simply appears.
 */
const useCountUp = (target: number, enabled: boolean, ms = 900) => {
  const [n, setN] = useState(enabled ? 0 : target)
  useEffect(() => {
    if (!enabled) { setN(target); return }
    let raf = 0
    const t0 = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / ms)
      setN(target * (1 - Math.pow(1 - p, 3)))   // ease-out cubic
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, enabled, ms])
  return n
}

const Metric: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
    <div className="text-sm font-semibold text-gray-900 tabular-nums truncate">{children}</div>
  </div>
)

interface CardProps {
  s: SponsorRollup
  colour: string
  /** This sponsor's share of all sponsor area-seconds in the library, 0-1. */
  share: number
  index: number
  customerId: string
  onSelect?: (partnerId: string) => void
}

const SponsorCard: React.FC<CardProps> = ({ s, colour, share, index, customerId, onSelect }) => {
  const reduce = !!useReducedMotion()
  const value = useCountUp(s.value ?? 0, !reduce && s.value !== null)
  const clickable = !!onSelect
  const Tag = clickable ? 'button' : 'div'

  return (
    // Motion lives on a wrapper so the card itself can stay a plain button.
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 12) * 0.05, ease: 'easeOut' }}
      whileHover={clickable && !reduce ? { y: -3 } : undefined}
      className="h-full"
    >
      <Tag
        {...(clickable ? { type: 'button' as const, onClick: () => onSelect!(s.id) } : {})}
        className={`group relative h-full w-full overflow-hidden text-left rounded-xl border border-gray-200 bg-white p-4
                    shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)] transition-shadow duration-300 ${
          clickable ? 'cursor-pointer hover:shadow-[0_2px_4px_rgba(16,24,40,0.06),0_16px_32px_-12px_rgba(16,24,40,0.22)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500' : ''
        }`}
      >
        {/* Depth: the sponsor's donut colour as an accent edge and a soft
            wash in the corner, so the card reads as that entity at a glance. */}
        <span aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: colour }} />
        <span aria-hidden
              className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-[0.10] blur-2xl transition-opacity duration-300 group-hover:opacity-20"
              style={{ backgroundColor: colour }} />

        <div className="relative flex items-center gap-3">
          <div className="rounded-lg shadow-sm ring-1 ring-black/5 transition-transform duration-300 group-hover:scale-105">
            <SponsorLogo id={s.id} name={s.name} customerId={customerId} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{s.name}</p>
            <p className="text-[11px] text-gray-500">
              {s.broadcasts} {s.broadcasts === 1 ? 'broadcast' : 'broadcasts'}
            </p>
          </div>
          {clickable && (
            <ArrowUpRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-all duration-300
                                     group-hover:text-blue-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          )}
        </div>

        {/* Share of all sponsor exposure, grown in on arrival. Area-seconds,
            not value, so an unpriced sponsor still gets a bar. */}
        <div className="relative mt-4">
          <div className="flex items-baseline justify-between text-[10px] mb-1">
            <span className="font-semibold uppercase tracking-wider text-gray-500">Share of sponsor exposure</span>
            <span className="font-semibold text-gray-700 tabular-nums">{Math.round(share * 100)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: colour }}
              initial={reduce ? false : { width: 0 }}
              animate={{ width: `${Math.max(share * 100, 1)}%` }}
              transition={{ duration: 0.8, delay: 0.15 + Math.min(index, 12) * 0.05, ease: 'easeOut' }}
            />
          </div>
        </div>

        <div className="relative grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100">
          <Metric label="Media value">
            {s.value === null ? (
              <span className="text-[11px] font-normal text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                Not priced
              </span>
            ) : (
              <span className="text-emerald-800">{formatMoney(value, s.currency)}</span>
            )}
          </Metric>
          <Metric label="Area-sec">{formatAreaSeconds(s.area)}</Metric>
          <Metric label="Broadcasts">{s.broadcasts}</Metric>
        </div>
        {/* Said, not hidden: a total over two of five broadcasts is a
            different claim from one over all five. */}
        {s.value !== null && s.priced < s.broadcasts && (
          <p className="relative text-[10px] text-gray-400 mt-2">
            Value from {s.priced} of {s.broadcasts} broadcasts; the rest are not priced.
          </p>
        )}
      </Tag>
    </motion.div>
  )
}

/**
 * A card per sponsor under the Overview donuts: logo, share of sponsor
 * exposure, media value, area-seconds and how many broadcasts they appeared in.
 */
const BroadcastSponsorCards: React.FC<Props> = ({ broadcasts, customerId, onSelect }) => {
  const sponsors = useMemo(() => rollup(broadcasts), [broadcasts])
  // The donut's colours, so a sponsor reads as the same entity in both places.
  const colours = useMemo(() => colourMap(broadcasts, (b) => b.sponsor_area), [broadcasts])
  const totalArea = useMemo(() => sponsors.reduce((n, s) => n + s.area, 0), [sponsors])

  if (sponsors.length === 0) return null

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-800">Sponsors</h2>
        <span className="text-xs text-gray-500">{sponsors.length} with exposure</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {sponsors.map((s, i) => (
          <SponsorCard key={s.id} s={s} index={i} customerId={customerId} onSelect={onSelect}
                       colour={colours.get(s.id) ?? OTHER_COLOR}
                       share={totalArea > 0 ? s.area / totalArea : 0} />
        ))}
      </div>
    </section>
  )
}

export default BroadcastSponsorCards
